/**
 * The person, at the foot of the sidebar: who is signed in and where, how the console looks, and
 * Sign out. One quiet control that opens a popover, as the organisation switcher does at the top;
 * this is sign-out's one home, in both editions.
 */

import { ArrowRightFromSquare, ChevronsExpandVertical } from '@gravity-ui/icons';
import { Avatar, Button, Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState, useSyncExternalStore } from 'react';

import { useAuth } from '../../auth/AuthProvider.tsx';
import { productShortName } from '../../edition.ts';
import { consoleDesignSystem, subscribeConsoleDesignSystem } from '../../orgDesignSystem.ts';
import { currentAppearance, type DesignSystem, setDesignSystem, subscribeAppearance } from '../../theme.ts';
import { organisationDetail } from '../cloud/OrganisationSwitcher.tsx';
import { ThemeSwitch } from './ThemeSwitch.tsx';

/** Up to two initials from a name or an address: "Jean-Pierre Simonis" is JS, "jp@mojoup.com.au" is J. */
function initials(name: string): string {
  const words = name.split('@')[0]!.split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

/** The person's picture from their sign-in, or their initials while there is none (or it fails to load). */
function PersonAvatar({ name, picture, size }: { name: string; picture?: string; size: 'sm' | 'md' }) {
  return (
    <Avatar size={size} color="accent" variant="soft" className="shrink-0">
      {picture ? <Avatar.Image src={picture} alt="" /> : null}
      <Avatar.Fallback>{initials(name)}</Avatar.Fallback>
    </Avatar>
  );
}

const DESIGN_TEXT: Record<DesignSystem, string> = { solid: 'Solid', glass: 'Glass' };

export function AccountMenu() {
  const { account, discovery, cloud, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const designSystem = useSyncExternalStore(subscribeAppearance, () => currentAppearance().designSystem);
  const organisationLook = useSyncExternalStore(subscribeConsoleDesignSystem, () => consoleDesignSystem().name);
  const name = account?.name || account?.email || 'Signed in';
  // Where the person is signed in: the organisation and its plan on Cloud, the organisation on Enterprise.
  const where = cloud ? `${cloud.organisation.name} · ${organisationDetail(cloud.organisation)}` : `${discovery?.organisation ?? ''}${discovery?.organisation ? ' · ' : ''}${productShortName()}`;
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Button variant="ghost" className="org-switcher" aria-label={`Account: ${name}. Appearance and sign out`}>
        <PersonAvatar name={name} picture={account?.picture} size="sm" />
        <span className="sidebar-collapse-hide min-w-0 flex-1 text-left">
          <span className="block truncate text-body font-medium">{name}</span>
          {account?.email && account.email !== name ? <span className="block truncate text-2xs text-muted">{account.email}</span> : null}
        </span>
        <ChevronsExpandVertical className="sidebar-collapse-hide size-3.5 shrink-0 text-muted" />
      </Button>
      <Popover.Content placement="top start" className="w-72 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="flex flex-col gap-3 p-3" aria-label="Your account">
          <div className="flex min-w-0 items-center gap-3">
            <PersonAvatar name={name} picture={account?.picture} size="md" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{account?.name || 'Signed in'}</div>
              {account?.email ? <div className="truncate text-xs text-muted">{account.email}</div> : null}
              <div className="mt-0.5 truncate text-xs text-muted">{where}</div>
            </div>
          </div>
          <div className="grid gap-1.5">
            <span className="pane-label">Colour mode</span>
            <ThemeSwitch />
          </div>
          <div className="grid gap-1.5">
            <span className="pane-label">Look</span>
            <Segment aria-label="Look" size="sm" className="w-full" selectedKey={designSystem} onSelectionChange={(k) => setDesignSystem(String(k) as DesignSystem)}>
              {(['solid', 'glass'] as DesignSystem[]).map((d) => (
                <Segment.Item key={d} id={d} className="flex-1 justify-center">
                  {DESIGN_TEXT[d]}
                </Segment.Item>
              ))}
            </Segment>
            <span className="text-xs text-muted">{organisationLook ? `Wearing ${organisationLook}, your organisation's design system` : 'In the Mojo Up design language'}</span>
          </div>
          <Button
            variant="tertiary"
            fullWidth
            onPress={() => {
              setOpen(false);
              signOut();
            }}
          >
            <ArrowRightFromSquare className="size-4" />
            Sign out
          </Button>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
